/**
 * Auth API Hooks
 * TanStack Query hooks for authentication domain
 */

import { useQuery, useMutation } from "@tanstack/react-query";
import { useSession } from "next-auth/react";
import { api } from "@/lib/api/client";

export interface UserProfile {
  id: string;
  name: string | null;
  email: string;
  phone: string | null;
  cpf: string | null;
  image: string | null;
  status: string;
  lastAccess: Date | null;
}

export interface ChangePasswordInput {
  currentPassword: string;
  newPassword: string;
}

export function useAuthMe() {
  return useQuery({
    queryKey: ["auth", "me"],
    queryFn: async () => {
      const response = await api.auth.getMe();
      // ✅ FIX: Garantir que sempre retornamos um valor válido
      const profile = response.data as UserProfile | null;
      if (!profile) {
        throw new Error("Usuário não autenticado");
      }
      return profile;
    },
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    staleTime: 0,
    retry: 1,
  });
}

export function useChangePassword(options?: {
  onSuccess?: () => void;
  onError?: (error: string) => void;
}) {
  const { data: session } = useSession();

  return useMutation({
    mutationFn: async (data: ChangePasswordInput) => {
      const userId = session?.user?.id;
      if (!userId) {
        throw new Error("Usuário não autenticado");
      }

      const response = await api.auth.changePassword(userId, {
        newPassword: data.newPassword,
      });
      return response.data;
    },
    onSuccess: () => {
      options?.onSuccess?.();
    },
    onError: (error: unknown) => {
      const errorMessage = error instanceof Error ? error.message : "Erro ao alterar senha";
      options?.onError?.(errorMessage);
    },
  });
}
